import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Home,
  Heart,
  Flower,
  Phone,
  Clock,
  Users,
  Shield,
  Star,
} from "lucide-react";

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);

  const services = [
    {
      id: 0,
      title: "Home Euthanasia",
      icon: Home,
      description: "Peaceful farewells in your pet's familiar environment",
      details: [
        "Contact us via website, email, or phone to arrange a visit",
        "Vet arrives with or without veterinary nurse (nurse availability may vary)",
        "Discussion of pet's overall health and decision confirmation",
        "Light sedation may be administered for pet's comfort",
        "You can be present throughout or leave the room if preferred",
        "Time for final goodbyes in a familiar, comfortable setting",
      ],
      features: [
        { icon: Clock, text: "Flexible scheduling" },
        { icon: Users, text: "Family involvement" },
        { icon: Shield, text: "Professional care" },
        { icon: Star, text: "Dignified process" },
      ],
    },
    {
      id: 1,
      title: "Quality of Life Assessment",
      icon: Heart,
      description: "Professional evaluation to help make informed decisions",
      details: [
        "Home visit assessment when you're unsure about timing",
        "Observation and clinical examination of your pet",
        "Review of previous medical records and history",
        "Discussion of overall clinical picture",
        "Exploration of alternative treatment options",
        "Time to consider options and make informed decisions",
      ],
      features: [
        { icon: Clock, text: "Comprehensive evaluation" },
        { icon: Users, text: "Expert consultation" },
        { icon: Shield, text: "Medical expertise" },
        { icon: Star, text: "Treatment alternatives" },
      ],
    },
    {
      id: 2,
      title: "Aftercare Services",
      icon: Flower,
      description: "Compassionate care for your pet after they've passed",
      details: [
        "Home burial guidance and local regulation compliance",
        "Direct transport to crematorium if preferred",
        "Arrangement with your preferred cremation company",
        "Shared cremation (no ashes returned)",
        "Individual cremation (guaranteed single pet)",
        "Price variations based on location and service type",
      ],
      features: [
        { icon: Clock, text: "Multiple options" },
        { icon: Users, text: "Personalized service" },
        { icon: Shield, text: "Professional handling" },
        { icon: Star, text: "Respectful care" },
      ],
    },
  ];

  const activeService = services[activeTab];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive end-of-life care designed to provide comfort,
              dignity, and peace for your beloved pets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <div className="flex flex-col sm:flex-row gap-2 mb-12">
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => setActiveTab(index)}
                className={`flex items-center space-x-3 px-6 py-4 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === index
                    ? "bg-primary-600 text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                }`}
              >
                <service.icon className="w-5 h-5" />
                <span>{service.title}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Left Side - Content */}
                <div className="p-8 lg:p-12">
                  <div className="flex items-start space-x-4 mb-8">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <activeService.icon className="w-8 h-8 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-3xl font-bold text-gray-900 mb-3">
                        {activeService.title}
                      </h2>
                      <p className="text-gray-600">
                        {activeService.description}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      What to Expect:
                    </h3>
                    <ul className="space-y-3">
                      {activeService.details.map((detail, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-600">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-4 mt-8">
                    {activeService.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <feature.icon className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-medium text-gray-700">
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex justify-center">
                      <a
                        href="/contact#contact-form"
                        className="btn-primary inline-flex items-center space-x-2"
                      >
                        <span>Contact Me</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Right Side - Visual */}
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <activeService.icon className="w-16 h-16 text-primary-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-primary-800 mb-4">
                      {activeService.title}
                    </h3>
                    <p className="text-primary-700 max-w-sm">
                      {activeService.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Additional Information */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Home Visits?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Home visits provide a more comfortable and less stressful
              experience for both pets and families.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Home,
                title: "Familiar Environment",
                description:
                  "Your pet remains in their comfortable, familiar surroundings, reducing stress and anxiety.",
              },
              {
                icon: Users,
                title: "Family Privacy",
                description:
                  "Say goodbye in the privacy of your own home, surrounded by family and familiar comforts.",
              },
              {
                icon: Shield,
                title: "Professional Care",
                description:
                  "Receive the same high-quality veterinary care with the added comfort of being at home.",
              },
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need Support or Guidance?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              I'm here to provide compassionate support and guidance during this
              difficult time. Every consultation is confidential and
              understanding.
            </p>
            <div className="flex justify-center">
              <a
                href="/contact#contact-form"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Contact Me
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
